import { useState } from "react";
import { FileText, Download, Loader2, TrendingUp, TrendingDown, BarChart3, Calendar as CalendarIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const QUICK_RANGES = [
  { id: "this_month", label: "Aktueller Monat" },
  { id: "last_month", label: "Letzter Monat" },
  { id: "ytd", label: "Jahr bis heute" },
  { id: "last_30", label: "Letzte 30 Tage" },
  { id: "last_90", label: "Letzte 90 Tage" },
  { id: "last_year", label: "Letztes Jahr" },
];

const computeRange = (id) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (id) {
    case "this_month":
      return { von: fmt(new Date(y, m, 1)), bis: fmt(new Date(y, m + 1, 0)) };
    case "last_month":
      return { von: fmt(new Date(y, m - 1, 1)), bis: fmt(new Date(y, m, 0)) };
    case "ytd":
      return { von: `${y}-01-01`, bis: todayISO() };
    case "last_30": {
      const v = new Date(now); v.setDate(v.getDate() - 29);
      return { von: fmt(v), bis: todayISO() };
    }
    case "last_90": {
      const v = new Date(now); v.setDate(v.getDate() - 89);
      return { von: fmt(v), bis: todayISO() };
    }
    case "last_year":
      return { von: `${y - 1}-01-01`, bis: `${y - 1}-12-31` };
    default:
      return { von: firstOfMonthISO(), bis: todayISO() };
  }
};

const BWATab = () => {
  const [bwa, setBwa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [von, setVon] = useState(firstOfMonthISO());
  const [bis, setBis] = useState(todayISO());

  const fetchBWA = async () => {
    if (!von || !bis) {
      toast.error("Bitte Von- und Bis-Datum wählen");
      return;
    }
    if (von > bis) {
      toast.error("Von-Datum muss vor Bis-Datum liegen");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tools/bwa?von=${von}&bis=${bis}`, { withCredentials: true });
      setBwa(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der BWA");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!von || !bis) return;
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/tools/bwa/pdf?von=${von}&bis=${bis}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BWA_${von}_bis_${bis}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("PDF-Erstellung fehlgeschlagen");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    if (!von || !bis) return;
    window.open(`${API}/tools/bwa/pdf?von=${von}&bis=${bis}`, "_blank");
  };

  const applyQuickRange = (id) => {
    const r = computeRange(id);
    setVon(r.von);
    setBis(r.bis);
  };

  const BWARow = ({ label, value, indent = 0, bold = false, highlight = false, negative = false, quote = null }) => (
    <div className={`flex items-center justify-between py-2 px-4 ${highlight ? 'bg-slate-700/30' : ''} ${bold ? 'border-t border-slate-600' : ''}`}>
      <span className={`${bold ? 'font-semibold text-white' : 'text-slate-300'} text-sm`}
        style={indent ? { paddingLeft: `${indent * 16}px` } : {}}>
        {label}
      </span>
      <div className="flex items-center gap-4">
        <span className={`font-mono text-sm ${bold ? 'font-semibold' : ''} ${
          highlight ? (value >= 0 ? 'text-emerald-400' : 'text-red-400') :
          negative ? 'text-red-400' : 'text-white'
        }`}>
          {negative && value > 0 ? '-' : ''}{formatCurrency(Math.abs(value || 0))}
        </span>
        {quote !== null && <span className="text-xs text-slate-500 w-16 text-right">{quote}%</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="bwa-tab">
      {/* Header + Zeitraumauswahl */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              <FileText size={18} className="text-emerald-400" />
              Betriebswirtschaftliche Auswertung (BWA)
            </h3>
            <p className="text-xs text-slate-400 mt-1">Kurzfristige Erfolgsrechnung nach DATEV-Standard · frei wählbarer Zeitraum</p>
          </div>
        </div>

        {/* Quick-Ranges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {QUICK_RANGES.map((r) => (
            <Button
              key={r.id}
              size="sm"
              variant="outline"
              onClick={() => applyQuickRange(r.id)}
              className="border-slate-600 hover:border-emerald-500 hover:bg-emerald-500/10 h-8 text-xs"
              data-testid={`bwa-quick-${r.id}`}
            >
              {r.label}
            </Button>
          ))}
        </div>

        {/* Datepicker */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <div>
            <Label htmlFor="bwa-von" className="text-xs text-slate-400 mb-1 flex items-center gap-1">
              <CalendarIcon size={12} /> Von
            </Label>
            <Input
              id="bwa-von"
              type="date"
              value={von}
              max={bis}
              onChange={(e) => setVon(e.target.value)}
              className="bg-slate-700 border-slate-600 h-9"
              data-testid="bwa-von-input"
            />
          </div>
          <div>
            <Label htmlFor="bwa-bis" className="text-xs text-slate-400 mb-1 flex items-center gap-1">
              <CalendarIcon size={12} /> Bis
            </Label>
            <Input
              id="bwa-bis"
              type="date"
              value={bis}
              min={von}
              onChange={(e) => setBis(e.target.value)}
              className="bg-slate-700 border-slate-600 h-9"
              data-testid="bwa-bis-input"
            />
          </div>
          <Button
            onClick={fetchBWA}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 h-9"
            data-testid="bwa-generate-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <BarChart3 size={16} className="mr-2" />}
            {loading ? "Erstelle..." : "BWA erstellen"}
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={!bwa || downloading}
            variant="outline"
            className="border-slate-600 hover:border-emerald-500 h-9"
            data-testid="bwa-pdf-btn"
            title="Als PDF herunterladen"
          >
            {downloading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* BWA Content */}
      {bwa && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          {/* BWA Header */}
          <div className="p-5 border-b border-slate-700 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-white">{bwa.titel}</h2>
              <p className="text-slate-400 text-sm">{bwa.untertitel}</p>
              {bwa.unternehmensform?.name && (
                <p className="text-xs text-violet-400 mt-1">Rechtsform: {bwa.unternehmensform.name} · Land: {bwa.land}</p>
              )}
            </div>
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="border-slate-600 hover:border-emerald-500"
              data-testid="bwa-print-btn"
            >
              <FileText size={14} className="mr-2" /> Drucken
            </Button>
          </div>

          {/* BWA Positionen */}
          <div className="divide-y divide-slate-700/50">
            {/* 1. Umsatzerloese */}
            <div>
              <div className="px-4 py-2 bg-slate-700/50">
                <span className="text-xs font-semibold text-slate-400 uppercase">{bwa.positionen["1_umsatzerloese"].titel}</span>
              </div>
              <BWARow label={`Speisen (${bwa.mwst_saetze?.speisen || 7}% ${bwa.land === 'AT' ? 'USt' : 'MwSt'})`} value={bwa.positionen["1_umsatzerloese"].speisen_mwst} indent={1} />
              <BWARow label={`Getraenke (${bwa.mwst_saetze?.getraenke || 19}% ${bwa.land === 'AT' ? 'USt' : 'MwSt'})`} value={bwa.positionen["1_umsatzerloese"].getraenke_mwst} indent={1} />
              <BWARow label="Sonstige Erloese" value={bwa.positionen["1_umsatzerloese"].sonstige_erloese} indent={1} />
              <BWARow label="Gesamtumsatz" value={bwa.positionen["1_umsatzerloese"].gesamt} bold />
            </div>

            {/* 2. Wareneinsatz */}
            <div>
              <div className="px-4 py-2 bg-slate-700/50">
                <span className="text-xs font-semibold text-slate-400 uppercase">{bwa.positionen["2_wareneinsatz"].titel}</span>
              </div>
              <BWARow label="Wareneinsatz Speisen (30%)" value={bwa.positionen["2_wareneinsatz"].wareneinsatz_speisen} indent={1} negative />
              <BWARow label="Wareneinsatz Getraenke (20%)" value={bwa.positionen["2_wareneinsatz"].wareneinsatz_getraenke} indent={1} negative />
              <BWARow label="Wareneinsatz gesamt" value={bwa.positionen["2_wareneinsatz"].gesamt} bold negative />
            </div>

            {/* 3. Rohertrag */}
            <BWARow label={bwa.positionen["3_rohertrag"].titel} value={bwa.positionen["3_rohertrag"].betrag} bold highlight
              quote={bwa.positionen["3_rohertrag"].quote} />

            {/* 4. Personalkosten */}
            <div>
              <div className="px-4 py-2 bg-slate-700/50">
                <span className="text-xs font-semibold text-slate-400 uppercase">{bwa.positionen["4_personalkosten"].titel}</span>
              </div>
              <BWARow label="Bruttloehne/-gehaelter" value={bwa.positionen["4_personalkosten"].brutto_loehne} indent={1} negative />
              <BWARow label="AG-Anteil Sozialversicherung" value={bwa.positionen["4_personalkosten"].ag_sozialversicherung} indent={1} negative />
              <BWARow label="Personalaufwand gesamt" value={bwa.positionen["4_personalkosten"].gesamt} bold negative
                quote={bwa.positionen["4_personalkosten"].quote} />
            </div>

            {/* 5. Sachkosten */}
            <div>
              <div className="px-4 py-2 bg-slate-700/50">
                <span className="text-xs font-semibold text-slate-400 uppercase">{bwa.positionen["5_sachkosten"].titel}</span>
              </div>
              <BWARow label="Miete / Pacht" value={bwa.positionen["5_sachkosten"].miete_pacht} indent={1} negative />
              <BWARow label="Energie / Nebenkosten" value={bwa.positionen["5_sachkosten"].energie_nebenkosten} indent={1} negative />
              <BWARow label="Versicherungen" value={bwa.positionen["5_sachkosten"].versicherungen} indent={1} negative />
              <BWARow label="Sonstige Aufwendungen" value={bwa.positionen["5_sachkosten"].sonstige} indent={1} negative />
              <BWARow label="Sachkosten gesamt" value={bwa.positionen["5_sachkosten"].gesamt} bold negative />
            </div>

            {/* 6-12. Ergebnisse */}
            <BWARow label={bwa.positionen["6_betriebsergebnis"].titel} value={bwa.positionen["6_betriebsergebnis"].betrag} bold highlight />
            <BWARow label={bwa.positionen["7_abschreibungen"].titel} value={bwa.positionen["7_abschreibungen"].betrag} negative />
            <BWARow label={bwa.positionen["8_ebit"].titel} value={bwa.positionen["8_ebit"].betrag} bold highlight />
            <BWARow label={bwa.positionen["9_zinsen"].titel} value={bwa.positionen["9_zinsen"].betrag} negative />
            <BWARow label={bwa.positionen["10_ergebnis_vor_steuern"].titel} value={bwa.positionen["10_ergebnis_vor_steuern"].betrag} bold highlight />
            <BWARow label={bwa.positionen["11_steuern"].titel} value={bwa.positionen["11_steuern"].betrag} negative />

            {/* Jahresueberschuss - hervorgehoben */}
            <div className={`px-4 py-3 ${bwa.positionen["12_jahresueberschuss"].betrag >= 0 ? 'bg-emerald-900/20' : 'bg-red-900/20'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-white flex items-center gap-2">
                  {bwa.positionen["12_jahresueberschuss"].betrag >= 0
                    ? <TrendingUp size={16} className="text-emerald-400" />
                    : <TrendingDown size={16} className="text-red-400" />}
                  {bwa.positionen["12_jahresueberschuss"].titel}
                </span>
                <span className={`font-mono font-bold text-lg ${bwa.positionen["12_jahresueberschuss"].betrag >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(bwa.positionen["12_jahresueberschuss"].betrag)}
                </span>
              </div>
            </div>
          </div>

          {/* Kennzahlen */}
          <div className="p-5 border-t border-slate-700">
            <h4 className="text-sm font-semibold text-white mb-3">Betriebswirtschaftliche Kennzahlen</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-slate-700/30 rounded-lg text-center">
                <p className="text-[10px] text-slate-400 uppercase">Rohertrag-Quote</p>
                <p className="text-lg font-bold text-white">{bwa.kennzahlen.rohertrag_quote}%</p>
                <p className="text-[10px] text-slate-500">Ziel: &gt;65%</p>
              </div>
              <div className="p-3 bg-slate-700/30 rounded-lg text-center">
                <p className="text-[10px] text-slate-400 uppercase">Wareneinsatz</p>
                <p className={`text-lg font-bold ${bwa.kennzahlen.wareneinsatzquote <= 30 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {bwa.kennzahlen.wareneinsatzquote}%
                </p>
                <p className="text-[10px] text-slate-500">Ziel: &lt;30%</p>
              </div>
              <div className="p-3 bg-slate-700/30 rounded-lg text-center">
                <p className="text-[10px] text-slate-400 uppercase">Personalkostenquote</p>
                <p className={`text-lg font-bold ${bwa.kennzahlen.personalkostenquote <= 35 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {bwa.kennzahlen.personalkostenquote}%
                </p>
                <p className="text-[10px] text-slate-500">Ziel: 25-35%</p>
              </div>
              <div className="p-3 bg-slate-700/30 rounded-lg text-center">
                <p className="text-[10px] text-slate-400 uppercase">Umsatzrendite</p>
                <p className={`text-lg font-bold ${bwa.kennzahlen.umsatzrendite > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {bwa.kennzahlen.umsatzrendite}%
                </p>
                <p className="text-[10px] text-slate-500">Ziel: &gt;5%</p>
              </div>
            </div>
          </div>

          {/* Hinweis */}
          <div className="px-5 pb-5">
            <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg text-xs text-amber-300">
              {bwa.hinweis}
            </div>
          </div>
        </div>
      )}

      {!bwa && !loading && (
        <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700">
          <FileText size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-white font-semibold">BWA erstellen</h3>
          <p className="text-slate-400 text-sm mt-2">Wähle einen Zeitraum oben und klicke auf „BWA erstellen". Anschließend kannst du das Ergebnis als PDF herunterladen oder drucken.</p>
        </div>
      )}
    </div>
  );
};

export default BWATab;
